import { defineItemStore } from "@editor/items/itemStore.store.js";

import { i18n } from "@/i18n";
import { showConfirm } from "@core/ui/alert/alert";
import { calculateExpressionValues } from "./indexFunctions";
import { mapValues } from "@/helpers/utility/CollectionFunctions";
import { changeElementType } from "../element/elementFunctions";

import { useRelationStore } from "@editor/items/relation/relations.store";
import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useSimulationStore } from "../simulation/simulations.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useSceneNodesStore } from "../scene/sceneNodes.store";
import { useRandomValuesStore } from "../expression/expressionRandomValues";

import type { ExpressionValue } from "../expression/expressionFunctions";
import type { IndexItem } from "./IndexItem";
import type { TargetItem } from "../target/TargetItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { ExpressionParseContext } from "../effect/Expression";

export const defaultIndexStructure: IndexItem = {
  type: "index",

  title: "",
  description: "",

  valueExpression: "",
  valueInputMode: "template",

  unit: null,

  id: 0,
  uid: "index_0",
  createdAt: 0
};

export const useIndexStore = defineItemStore("model/indices", defaultIndexStructure, {
  getters: {
    indexParseContext(): ExpressionParseContext {
      const settings = useSettingsStore();
      return {
        targets: useTargetStore().items,
        resources: useResourceStore().items,
        indices: this.items,
        roundIndex: 0,
        simulationSettings: {
          roundUnit: settings.simulationRoundUnit,
          roundLength: settings.simulationRoundLength,
          roundAmount: settings.simulationRoundAmount,
          timeStart: settings.simulationTimeStart,
          durationType: settings.simulationDurationType,
        }
      };
    },
    expressionValues(): {
      [indexUid: string]: ExpressionValue;
    } {
      return calculateExpressionValues(this.items, {
        ...this.indexParseContext,
        randomValues: useRandomValuesStore().randomValues
      });
    },
    values(): { [indexId: number]: number } {
      return mapValues(this.items, (item) => {
        const value = this.expressionValues[item.uid].value;
        return Number(value);
      });
    },
    units(): { [indexId: number]: string } {
      return mapValues(this.items, (item) => {
        const unit = item.unit;
        if (unit) return unit;
        const uid = item.uid;
        return this.expressionValues[uid].unit;
      });
    },
    getByLinkedElement() {
      return (elementUid: string): IndexItem[] => this.orderedItems.filter((index) => {
        const expression = index.valueExpression;
        return expression.includes(elementUid);
      });
    }
  },
  events: {
    async canBeDeleted(index) {
      const inSimulations = useSimulationStore().orderedItems.some((simulation) => {
        const elementIds = Object.keys(simulation.startElementValues);
        return elementIds.includes(index.uid);
      });
      if (!inSimulations) return true;
      return showConfirm(
        i18n.t("questions.deleteIndex"),
        i18n.t("actions.deleteIndex"),
        i18n.t("labels.abort")
      );
    },
    async afterDelete(index): Promise<unknown> {
      const uid = index.uid;
      const promises = [];

      // delete all connected RELATIONS
      const relationStore = useRelationStore();
      const connectedRelations = relationStore.orderedItems.filter(
        (relation) =>
          relation.startItemType === "index" &&
          relation.startItemId === index.id
      );
      if (connectedRelations != null) {
        for (const relation of connectedRelations) {
          promises.push(
            relationStore.delete({ id: relation.id, instant: true })
          );
        }
      }

      const sceneNodeStore = useSceneNodesStore();
      const nodes = sceneNodeStore.byNodeType.element ?? [];
      const indexNodes = nodes.filter((node) => node.data.elementUid === uid);
      for (const node of indexNodes) {
        promises.push(
          sceneNodeStore.delete({ id: node.id, instant: true })
        );
      }

      return Promise.all(promises);

      // end of deletion event
    }
  },
  actions: {
    async changeType(id: number, type: "target" | "resource"): Promise<TargetItem | ResourceItem | undefined> {
      const index = this.items[id];
      if (index == null) return;

      const newElement = await changeElementType(index, type);

      await this.delete({ id: index.id, instant: true });
      return newElement;
    },

    async duplicate(id: number, changes: Partial<IndexItem> = {}) {
      return await this.super.duplicate(id, {
        ...changes
      });
    }
  }
});
